import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { applyEnvelope, type Envelope, type Notice, type NoticeLevel } from '../../lib/envelope'
import { characterKeys } from './queries'
import type { CharacterSections, CharacterSheet } from './types'

type Schema = components['schemas']

/**
 * Every character mutation answers with the same envelope, so every mutation is
 * wired the same way: call, patch the cache with `changed`, surface `notices`.
 * There is no refetch and no invalidation anywhere in this file — that is the
 * whole point of the contract (see lib/envelope.ts).
 */
export type CharacterEnvelope = Envelope<CharacterSections>

const NOTICE_LEVELS: NoticeLevel[] = ['info', 'warn', 'error']

function toNotice(raw: Schema['Notice']): Notice {
  const level = raw.level as NoticeLevel | undefined
  return {
    level: level !== undefined && NOTICE_LEVELS.includes(level) ? level : 'info',
    code: raw.code ?? 'unknown',
    message: raw.message ?? '',
  }
}

/** Narrows the wire envelope. The `changed` bag is untyped for the same reason `sections` is. */
export function toEnvelope(dto: Schema['CharacterEnvelope']): CharacterEnvelope {
  return {
    version: dto.version ?? 0,
    changed: (dto.changed ?? {}) as Partial<CharacterSections>,
    notices: (dto.notices ?? []).map(toNotice),
  }
}

/**
 * The cache key to patch is the one the screen *asked for*, not the one the
 * envelope came back with. A sheet opened without a shape is cached under
 * `shapeId: null` even though the server resolved it to a real shape, and
 * patching the resolved key would leave the visible sheet stale.
 */
function patchSheet(
  queryClient: QueryClient,
  characterId: number,
  requestedShapeId: number | null,
  envelope: CharacterEnvelope,
): void {
  queryClient.setQueryData<CharacterSheet>(
    characterKeys.sheet(characterId, requestedShapeId),
    (previous) => (previous === undefined ? previous : applyEnvelope(previous, envelope)),
  )
}

export interface SectionMutationOptions<TVariables> {
  characterId: number
  /** The shape the screen is showing; null means "the server's default". */
  shapeId: number | null
  request: (variables: TVariables) => Promise<Schema['CharacterEnvelope']>
  /**
   * Optimistic patch. Return the sections to apply immediately.
   *
   * Reserved for changes that are a single bit with an obvious inverse — marking
   * a stress box, spending a fate point. Everything else cascades into budgets
   * the server computes and the client is forbidden to derive, so guessing at
   * them would mean rendering numbers that may be wrong.
   */
  optimistic?: (variables: TVariables, sheet: CharacterSheet) => Partial<CharacterSections>
  /** Receives every successful envelope, so one place can render all notices. */
  onEnvelope?: (envelope: CharacterEnvelope) => void
  /** Receives every failure, so one place can surface 409s and the rest. */
  onFailure?: (error: unknown) => void
}

interface MutationContext {
  previous: CharacterSheet | undefined
}

export function useSectionMutation<TVariables>(options: SectionMutationOptions<TVariables>) {
  const queryClient = useQueryClient()
  const key = characterKeys.sheet(options.characterId, options.shapeId)

  return useMutation<CharacterEnvelope, unknown, TVariables, MutationContext>({
    mutationFn: async (variables) => toEnvelope(await options.request(variables)),

    onMutate: async (variables) => {
      if (options.optimistic === undefined) return { previous: undefined }

      // Without this, a read already in flight can land after the optimistic
      // patch and quietly undo it.
      await queryClient.cancelQueries({ queryKey: key })

      const previous = queryClient.getQueryData<CharacterSheet>(key)
      if (previous === undefined) return { previous: undefined }

      const changed = options.optimistic(variables, previous)
      queryClient.setQueryData<CharacterSheet>(key, {
        ...previous,
        sections: { ...previous.sections, ...changed },
      })

      return { previous }
    },

    onError: (error, _variables, context) => {
      // Put back exactly what was there. The server rejected the change, so the
      // optimistic guess is not just stale — it never happened.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous)
      }
      options.onFailure?.(error)
    },

    onSuccess: (envelope) => {
      patchSheet(queryClient, options.characterId, options.shapeId, envelope)
      options.onEnvelope?.(envelope)
    },
  })
}

// ---------------------------------------------------------------------------
// Request builders — one per endpoint, each returning the raw envelope DTO.
//
// Shape-scoped endpoints take ?shapeId=; the ones addressed by a slot or item id
// resolve the shape from that row instead, which is why they take no query.
// ---------------------------------------------------------------------------

type Envelopes = Schema['CharacterEnvelope']

const shapeQuery = (shapeId: number | null) => (shapeId === null ? undefined : { shapeId })

export const characterApi = {
  setStressMark: (characterId: number, slotId: number, isStressed: boolean) =>
    api.put<Envelopes>(`/characters/${characterId}/stress/${slotId}`, { isStressed }),

  addStressBox: (characterId: number, shapeId: number | null, type: string) =>
    api.post<Envelopes>(
      `/characters/${characterId}/stress`,
      { type },
      { query: shapeQuery(shapeId) },
    ),

  removeStressBox: (characterId: number, slotId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/stress/${slotId}`),

  clearStress: (characterId: number, shapeId: number | null) =>
    api.post<Envelopes>(`/characters/${characterId}/stress/clear`, undefined, {
      query: shapeQuery(shapeId),
    }),

  feed: (characterId: number, shapeId: number | null) =>
    api.post<Envelopes>(`/characters/${characterId}/stress/feed`, undefined, {
      query: shapeQuery(shapeId),
    }),

  setFatePoints: (characterId: number, fatePoints: number) =>
    api.put<Envelopes>(`/characters/${characterId}/fate`, { fatePoints }),

  setTotalRefresh: (characterId: number, totalRefresh: number) =>
    api.put<Envelopes>(`/characters/${characterId}/fate/refresh`, { totalRefresh }),

  setSkillLevel: (characterId: number, shapeId: number | null, skillId: number, level: number) =>
    api.put<Envelopes>(
      `/characters/${characterId}/skills/${skillId}`,
      { level },
      {
        query: shapeQuery(shapeId),
      },
    ),

  removeSkill: (characterId: number, shapeId: number | null, skillId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/skills/${skillId}`, {
      query: shapeQuery(shapeId),
    }),

  setMaxSkillLevel: (characterId: number, shapeId: number | null, maxLevel: number) =>
    api.put<Envelopes>(
      `/characters/${characterId}/skills/max-level`,
      { maxLevel },
      {
        query: shapeQuery(shapeId),
      },
    ),

  setSkillPool: (characterId: number, shapeId: number | null, poolOption: string) =>
    api.put<Envelopes>(
      `/characters/${characterId}/skills/pool`,
      { poolOption },
      {
        query: shapeQuery(shapeId),
      },
    ),

  adjustSkillPool: (characterId: number, shapeId: number | null, delta: number) =>
    api.post<Envelopes>(
      `/characters/${characterId}/skills/pool/adjust`,
      { delta },
      {
        query: shapeQuery(shapeId),
      },
    ),

  updateAspects: (characterId: number, body: Schema['UpdateAspectsRequest']) =>
    api.put<Envelopes>(`/characters/${characterId}/aspects`, body),

  updateBio: (characterId: number, body: Schema['UpdateBioRequest']) =>
    api.put<Envelopes>(`/characters/${characterId}/bio`, body),

  setConsequence: (characterId: number, slotId: number, name: string, effect: string | null) =>
    api.put<Envelopes>(`/characters/${characterId}/consequences/${slotId}`, { name, effect }),

  clearConsequence: (characterId: number, slotId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/consequences/${slotId}/consequence`),

  addConsequenceSlot: (characterId: number, shapeId: number | null, type: string, level: string) =>
    api.post<Envelopes>(
      `/characters/${characterId}/consequences`,
      { type, level },
      {
        query: shapeQuery(shapeId),
      },
    ),

  removeConsequenceSlot: (characterId: number, slotId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/consequences/${slotId}`),

  addAbilityFromTemplate: (characterId: number, shapeId: number | null, abilityId: number) =>
    api.post<Envelopes>(
      `/characters/${characterId}/abilities/from-template/${abilityId}`,
      undefined,
      {
        query: shapeQuery(shapeId),
      },
    ),

  addCustomAbility: (
    characterId: number,
    shapeId: number | null,
    body: Schema['AddCustomAbilityRequest'],
  ) =>
    api.post<Envelopes>(`/characters/${characterId}/abilities`, body, {
      query: shapeQuery(shapeId),
    }),

  removeAbility: (characterId: number, shapeId: number | null, abilityId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/abilities/${abilityId}`, {
      query: shapeQuery(shapeId),
    }),

  setAbilityShapes: (
    characterId: number,
    shapeId: number | null,
    abilityId: number,
    shapeIds: number[],
  ) =>
    api.put<Envelopes>(
      `/characters/${characterId}/abilities/${abilityId}/shapes`,
      { shapeIds },
      {
        query: shapeQuery(shapeId),
      },
    ),

  addAbilityExtraStress: (
    characterId: number,
    shapeId: number | null,
    abilityId: number,
    type: string,
    value: number,
  ) =>
    api.post<Envelopes>(
      `/characters/${characterId}/abilities/${abilityId}/extra-stress`,
      { type, value },
      { query: shapeQuery(shapeId) },
    ),

  setAbilityVampirismRelated: (
    characterId: number,
    shapeId: number | null,
    abilityId: number,
    isVampirismRelated: boolean,
  ) =>
    api.put<Envelopes>(
      `/characters/${characterId}/abilities/${abilityId}/vampirism-related`,
      { isVampirismRelated },
      { query: shapeQuery(shapeId) },
    ),

  removeAbilityExtraStress: (characterId: number, shapeId: number | null, extraStressId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/abilities/extra-stress/${extraStressId}`, {
      query: shapeQuery(shapeId),
    }),

  addAbilityExtraConsequence: (
    characterId: number,
    shapeId: number | null,
    abilityId: number,
    type: string,
    level: string,
  ) =>
    api.post<Envelopes>(
      `/characters/${characterId}/abilities/${abilityId}/extra-consequence`,
      { type, level },
      { query: shapeQuery(shapeId) },
    ),

  removeAbilityExtraConsequence: (
    characterId: number,
    shapeId: number | null,
    extraConsequenceId: number,
  ) =>
    api.delete<Envelopes>(
      `/characters/${characterId}/abilities/extra-consequence/${extraConsequenceId}`,
      { query: shapeQuery(shapeId) },
    ),

  addEquipment: (characterId: number, body: Schema['EquipmentRequest']) =>
    api.post<Envelopes>(`/characters/${characterId}/equipment`, body),

  updateEquipment: (characterId: number, itemId: number, body: Schema['EquipmentRequest']) =>
    api.put<Envelopes>(`/characters/${characterId}/equipment/${itemId}`, body),

  setEquipped: (characterId: number, itemId: number, isEquipped: boolean) =>
    api.put<Envelopes>(`/characters/${characterId}/equipment/${itemId}/equipped`, { isEquipped }),

  removeEquipment: (characterId: number, itemId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/equipment/${itemId}`),

  addFocusItem: (characterId: number, body: Schema['FocusItemRequest']) =>
    api.post<Envelopes>(`/characters/${characterId}/focus-items`, body),

  removeFocusItem: (characterId: number, itemId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/focus-items/${itemId}`),

  addEnchantedItem: (characterId: number, body: Schema['EnchantedItemRequest']) =>
    api.post<Envelopes>(`/characters/${characterId}/enchanted-items`, body),

  useEnchantedItem: (characterId: number, itemId: number) =>
    api.post<Envelopes>(`/characters/${characterId}/enchanted-items/${itemId}/use`),

  refreshEnchantedItem: (characterId: number, itemId: number) =>
    api.post<Envelopes>(`/characters/${characterId}/enchanted-items/${itemId}/refresh`),

  refreshAllEnchantedItems: (characterId: number) =>
    api.post<Envelopes>(`/characters/${characterId}/enchanted-items/refresh`),

  removeEnchantedItem: (characterId: number, itemId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/enchanted-items/${itemId}`),

  addRoteSpell: (characterId: number, body: Schema['RoteSpellRequest']) =>
    api.post<Envelopes>(`/characters/${characterId}/rote-spells`, body),

  removeRoteSpell: (characterId: number, spellId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/rote-spells/${spellId}`),

  addSpecialization: (characterId: number, body: Schema['SpecializationRequest']) =>
    api.post<Envelopes>(`/characters/${characterId}/specializations`, body),

  removeSpecialization: (characterId: number, specializationId: number) =>
    api.delete<Envelopes>(`/characters/${characterId}/specializations/${specializationId}`),
}

// ---------------------------------------------------------------------------
// Optimistic patches — only for the two single-bit changes.
// ---------------------------------------------------------------------------

/** Flips one stress box in place, leaving every other section alone. */
export function optimisticStress(
  variables: { slotId: number; isStressed: boolean },
  sheet: CharacterSheet,
): Partial<CharacterSections> {
  const stress = sheet.sections.stress
  if (stress === undefined) return {}

  return {
    stress: {
      ...stress,
      tracks: (stress.tracks ?? []).map((track) => ({
        ...track,
        boxes: (track.boxes ?? []).map((box) =>
          box.id === variables.slotId ? { ...box, isStressed: variables.isStressed } : box,
        ),
      })),
    },
  }
}

/**
 * Sets the fate-point count.
 *
 * Only `fatePoints` is touched. The refresh figures beside it are server-computed
 * from the character's abilities and do not move when a point is spent, so
 * guessing at them here would be inventing data.
 */
export function optimisticFate(
  variables: { fatePoints: number },
  sheet: CharacterSheet,
): Partial<CharacterSections> {
  const fate = sheet.sections.fate
  if (fate === undefined) return {}

  return { fate: { ...fate, fatePoints: variables.fatePoints } }
}
